/**
 * Der Anmeldezustand als Kontext — und die Brücke zu `lib/api.ts`.
 *
 * Hier steht bewusst kein JSX: Das Modul wird von Komponenten *und* von der
 * Transportschicht benutzt, und ein Kontextobjekt neben einer Komponente in
 * derselben Datei kostet bei jedem Speichern den Hot-Reload-Zustand.
 *
 * `lib/api.ts` weiß nichts über Supabase — es fragt nur nach dem aktuellen
 * Token. Diese Datei ist die einzige Stelle, an der beide Seiten sich treffen.
 */

import { createContext, useContext } from 'react'
import type { QueryClient } from '@tanstack/react-query'

import { setSessionGenerationSource, setTokenSource } from '@/lib/api'

/**
 * Das aktuelle Zugangstoken.
 *
 * Modulzustand statt `useRef`: Die Frage kommt aus einem `fetch`, das zu einem
 * beliebigen Zeitpunkt läuft — auch während eines Rerenders. Ein Ref wäre
 * dabei genau einen Tick zu alt.
 */
let currentToken: string | null = null
let currentUserId: string | null = null
let sessionGeneration = 0

setTokenSource(() => currentToken)
setSessionGenerationSource(() => sessionGeneration)

/**
 * Aktualisiert den Token und markiert nur eine neue Identität als neue
 * Sitzung. Ein Token-Refresh derselben Person lässt laufende Requests zu.
 */
export function rememberSession(token: string | null, userId: string | null): boolean {
  const changed = currentUserId !== userId
  currentToken = token
  if (changed) {
    currentUserId = userId
    sessionGeneration += 1
  }
  return changed
}

/** Entfernt alle privaten Read- und Mutationseinträge am Sitzungsrand. */
export function discardSessionData(queryClient: QueryClient): void {
  void queryClient.cancelQueries()
  queryClient.clear()
}

/**
 * `pending` ist nicht dasselbe wie `anonymous`: Beim Start liest Supabase die
 * gespeicherte Sitzung asynchron. Wer die beiden zusammenwirft, zeigt jedem
 * angemeldeten Nutzer für einen Moment das Anmeldeformular.
 */
export type AuthStatus = 'pending' | 'anonymous' | 'authenticated'

export interface AuthState {
  readonly status: AuthStatus
  readonly email: string | null
  /** Wirft mit einer fertigen deutschen Meldung, wenn es nicht klappt. */
  readonly signIn: (email: string, password: string) => Promise<void>
  readonly signOut: () => Promise<void>
}

export const AuthContext = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
  const state = useContext(AuthContext)
  if (state === null) {
    throw new Error('useAuth wurde außerhalb von <AuthProvider> benutzt.')
  }
  return state
}
